// File Service API - File Upload Integration
import { apiPost, apiGet, apiDelete } from '../services/apiClient';

/**
 * Resolves the logged-in user id from the same session keys used after login (authService / portal).
 * @returns {number|null}
 */
function getSessionNumericUserId() {
  try {
    const fromKey = sessionStorage.getItem('userId');
    if (fromKey != null && fromKey !== '' && fromKey !== 'undefined' && fromKey !== 'null') {
      const n = parseInt(String(fromKey).trim(), 10);
      if (!Number.isNaN(n) && n > 0) {
        return n;
      }
    }
    const authUserRaw = sessionStorage.getItem('authUser');
    if (authUserRaw) {
      const u = JSON.parse(authUserRaw);
      const candidates = [u?.userId, u?.id, u?.user_id];
      for (const c of candidates) {
        if (c != null && c !== '') {
          const n = typeof c === 'number' ? c : parseInt(String(c).trim(), 10);
          if (!Number.isNaN(n) && n > 0) {
            return n;
          }
        }
      }
    }
    const authSessionRaw = sessionStorage.getItem('authSession');
    if (authSessionRaw) {
      const s = JSON.parse(authSessionRaw);
      const c = s?.userId;
      if (c != null && c !== '') {
        const n = typeof c === 'number' ? c : parseInt(String(c).trim(), 10);
        if (!Number.isNaN(n) && n > 0) {
          return n;
        }
      }
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Upload file to file service
 * POST /gateway/file-service/jwt/file/upload
 * 
 * @param {File} file - The file to upload
 * @param {Object} params - Upload parameters
 * @param {number} params.reg_id - Registration/Ticket ID
 * @param {number} params.fileTypeId - File type ID (default: 1)
 * @param {boolean} params.isActive - Is active (default: true)
 * @param {string} params.screenName - Screen name (e.g., "TICKET")
 * @param {number} [params.id] - Optional ID for update
 * @param {number} [params.userId] - Optional uploader id (defaults to session user when omitted; sent as form field for hd-fileservice fallback)
 * @returns {Promise<Object>} Response with document_path and ticket_number
 */
export const uploadFile = async (file, params = {}) => {
  const {
    reg_id,
    fileTypeId,
    isActive = true,
    screenName = 'TICKET',
    id,
    userId: explicitUserId
  } = params;

  if (!file) {
    throw new Error('File is required');
  }

  // Client-side size check: 5 MB
  const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const err = new Error('File size exceeds 5 MB. Please select a smaller file.');
    err.code = 'FILE_TOO_LARGE';
    throw err;
  }

  if (!reg_id) {
    throw new Error('reg_id is required');
  }

  // Create FormData
  const formData = new FormData();
  formData.append('file', file);
  // Only send fileTypeId when explicitly provided. For PROFILE, backend resolves type.
  if (typeof fileTypeId !== 'undefined' && fileTypeId !== null) {
    formData.append('fileTypeId', String(fileTypeId));
  }
  formData.append('isActive', isActive.toString());
  formData.append('reg_id', reg_id.toString());
  // formData.append('screenName', screenName);

  if (id) {
    formData.append('id', id.toString());
  }

  const resolvedUserId =
    explicitUserId != null && explicitUserId !== ''
      ? (typeof explicitUserId === 'number' ? explicitUserId : parseInt(String(explicitUserId), 10))
      : getSessionNumericUserId();
  if (resolvedUserId != null && !Number.isNaN(resolvedUserId) && resolvedUserId > 0) {
    formData.append('userId', String(resolvedUserId));
  }

  try {
    // Send reg_id and screenName as query parameters so they're available even when multipart parsing fails
    // This ensures the backend exception handler can access these parameters for rollback
    const response = await apiPost('/gateway/file-service/jwt/file/upload', formData, {
      headers: {
        'Accept': 'application/json, text/plain, */*'
      },
      params: { // These will be appended to the URL as query parameters
        reg_id: reg_id.toString(),
        screenName: screenName
      }
    });

    // Response structure: { status, message, data, success, timestamp }
    return response;
  } catch (error) {
    console.error('File upload error:', error);
    throw error;
  }
};

/**
 * Get profile files for a user (most recent first)
 * GET /gateway/file-service/jwt/file/get/profilefiles/{userId}
 * Returns array of objects with document_path
 */
export const getProfileFiles = async (userId) => {
  if (!userId) throw new Error('userId is required');
  const resp = await apiGet(`/gateway/file-service/jwt/file/get/profilefiles/${userId}`);
  // Response may be wrapped in { data: [...] }
  const data = Array.isArray(resp?.response)
    ? resp.response
    : (Array.isArray(resp?.data?.response) ? resp.data.response : []);
  return data;
};

/**
 * Convenience: get active/latest profile image URL (absolute)
 */
export const getActiveProfileImageUrl = async (userId) => {
  // Prefer env base; fallback to current origin for local/dev
  // const DOMAIN_URL = process.env.REACT_APP_BASE_URL || window.location.origin;
  const DOMAIN_URL = 'https://helpdesk-pro.dhanushinfotech.com';
  const files = await getProfileFiles(userId);
  if (!files || files.length === 0) return null;
  const path = files[0]?.document_path || files[0];
  if (!path) return null;
  //console.log("FINAL IMAGE URL:", path);
  //console.log("DOMAIN_URL:", DOMAIN_URL);
  return `${DOMAIN_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

/**
 * File Service API
 */
export const fileService = {
  uploadFile,
  getProfileFiles,
  getActiveProfileImageUrl
};

export default fileService;

// Delete current active profile photo
export const deleteProfilePhoto = async (userId, hardDelete = false) => {
  if (!userId) throw new Error('userId is required');
  const resp = await apiDelete(`/gateway/file-service/jwt/file/profile/${userId}`, {
    params: { hardDelete: String(Boolean(hardDelete)) }
  });
  return resp;
};
